import { Controller } from "@hotwired/stimulus"

// Enhances a native <select> into a searchable combobox in place. The native
// <select> stays in the DOM (visually hidden) so the form still submits its
// value and any existing `change->` actions on it keep firing. Progressive
// enhancement: if this controller never connects, the native select still works.
//
// Applied automatically to admin selects (see lib/select_search_auto.js).
// Opt out with data-searchable="false"; force on a short select with
// data-searchable="true".
export default class extends Controller {
  connect() {
    const select = this.element
    if (select.multiple || select.dataset.ssReady === "1") return
    select.dataset.ssReady = "1"
    this.select = select
    this.activeIndex = -1
    this.buildUi()
    this.syncFromSelect()

    this.onDocClick = (e) => { if (!this.wrapper.contains(e.target) && !this.list.contains(e.target)) this.close() }
    document.addEventListener("click", this.onDocClick)

    // Keep in sync when another controller repopulates the native select's
    // options client-side (e.g. cascading car → version → model pickers).
    this.observer = new MutationObserver(() => { this.renderOptions(); this.syncFromSelect() })
    this.observer.observe(this.select, { childList: true })
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.onDocClick) document.removeEventListener("click", this.onDocClick)
    if (this._reposition) {
      window.removeEventListener("scroll", this._reposition, true)
      window.removeEventListener("resize", this._reposition)
      this._reposition = null
    }
    // Remove only the sibling UI; never move the select (see buildUi).
    if (this.list) { this.list.remove(); this.list = null }
    if (this.wrapper) { this.wrapper.remove(); this.wrapper = null }
    this.select.classList.remove("sr-only")
    this.select.removeAttribute("aria-hidden")
    this.select.removeAttribute("tabindex")
    delete this.select.dataset.ssReady
  }

  buildUi() {
    const select = this.select

    // Hide the native select but keep it in place + submittable.
    select.classList.add("sr-only")
    select.setAttribute("aria-hidden", "true")
    select.setAttribute("tabindex", "-1")

    // IMPORTANT: insert the combobox UI as a sibling right AFTER the select —
    // do NOT move the select into a wrapper. Moving this.element makes Stimulus
    // disconnect/reconnect the controller, which (with disconnect unwrapping)
    // becomes an infinite loop that freezes the browser.
    const wrapper = document.createElement("div")
    wrapper.className = "relative"
    wrapper.dataset.ssWrap = "1"
    select.insertAdjacentElement("afterend", wrapper)
    this.wrapper = wrapper

    const input = document.createElement("input")
    input.type = "text"
    input.autocomplete = "off"
    input.spellcheck = false
    input.className = this.inputClass()
    input.disabled = select.disabled
    input.placeholder = select.dataset.ssPlaceholder || this.blankLabel() || "Buscar…"
    input.addEventListener("focus", () => this.openAll())
    input.addEventListener("click", () => this.openAll())
    input.addEventListener("input", () => this.onSearch())
    input.addEventListener("keydown", (e) => this.onKeydown(e))
    wrapper.appendChild(input)
    this.input = input

    // Position is fixed (set in position()) so the list escapes any ancestor
    // with overflow:hidden (e.g. .detail-card) instead of being clipped.
    const list = document.createElement("ul")
    list.className = "hidden fixed z-50 max-h-64 overflow-auto rounded-lg border border-base-300 bg-base-100 shadow-lg"
    list.dataset.ssList = "1"
    // Inside a daisyUI modal the .modal-box is transformed (+ overflow:auto), which
    // makes it the containing block for fixed descendants and clips the list. Mount
    // the list on the <dialog> instead (no transform), keeping viewport positioning.
    const dialog = select.closest("dialog")
    ;(dialog || wrapper).appendChild(list)
    this.list = list

    this.renderOptions()
  }

  renderOptions() {
    this.list.replaceChildren()
    this.optionEls = []
    Array.from(this.select.options).forEach((opt) => {
      const li = document.createElement("li")
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "block w-full px-3 py-2 text-left text-sm hover:bg-base-200 disabled:opacity-40"
      btn.textContent = opt.text
      btn.dataset.value = opt.value
      btn.dataset.search = opt.text.trim().toLowerCase()
      btn.disabled = opt.disabled
      btn.addEventListener("click", () => this.choose(opt))
      li.appendChild(btn)
      this.list.appendChild(li)
      this.optionEls.push(btn)
    })
    const empty = document.createElement("li")
    empty.className = "hidden px-3 py-2 text-sm text-base-content/50"
    empty.textContent = this.select.dataset.ssEmpty || "Nenhum resultado"
    this.list.appendChild(empty)
    this.emptyEl = empty
  }

  syncFromSelect() {
    const opt = this.select.options[this.select.selectedIndex]
    this.input.value = opt && opt.value !== "" ? opt.text : ""
  }

  choose(opt) {
    this.select.value = opt.value
    this.input.value = opt.value !== "" ? opt.text : ""
    this.select.dispatchEvent(new Event("input", { bubbles: true }))
    this.select.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
    this.input.focus()
  }

  onSearch() {
    const q = this.input.value.trim().toLowerCase()
    let visible = 0
    this.optionEls.forEach((btn) => {
      const show = q === "" || btn.dataset.search.includes(q)
      btn.parentElement.classList.toggle("hidden", !show)
      if (show) visible++
    })
    this.emptyEl.classList.toggle("hidden", visible !== 0)
    this.activeIndex = -1
    this.open()
  }

  openAll() {
    if (this.select.disabled) return
    this.optionEls.forEach((b) => b.parentElement.classList.remove("hidden"))
    this.emptyEl.classList.add("hidden")
    this.open()
    this.input.select()
  }

  onKeydown(e) {
    if (e.key === "Escape") return this.close()
    const shown = this.optionEls.filter((b) => !b.parentElement.classList.contains("hidden") && !b.disabled)
    if (e.key === "Enter" && this.isOpen()) {
      e.preventDefault()
      const target = shown[this.activeIndex] || shown[0]
      if (target) target.click()
    } else if (e.key === "ArrowDown") {
      e.preventDefault()
      this.open()
      this.activeIndex = Math.min(this.activeIndex + 1, shown.length - 1)
      this.highlight(shown)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, 0)
      this.highlight(shown)
    }
  }

  highlight(shown) {
    this.optionEls.forEach((b) => b.classList.remove("bg-base-200"))
    const el = shown[this.activeIndex]
    if (el) { el.classList.add("bg-base-200"); el.scrollIntoView({ block: "nearest" }) }
  }

  open() {
    this.list.classList.remove("hidden")
    this.position()
    if (!this._reposition) {
      this._reposition = () => this.position()
      // capture:true so we also catch scrolls on inner scroll containers.
      window.addEventListener("scroll", this._reposition, true)
      window.addEventListener("resize", this._reposition)
    }
  }

  close() {
    this.list.classList.add("hidden")
    if (this._reposition) {
      window.removeEventListener("scroll", this._reposition, true)
      window.removeEventListener("resize", this._reposition)
      this._reposition = null
    }
  }

  // Anchor the fixed-position list to the input, flipping above when there's
  // little room below.
  position() {
    const r = this.input.getBoundingClientRect()
    const gap = 4
    const spaceBelow = window.innerHeight - r.bottom
    const spaceAbove = r.top
    const openUp = spaceBelow < 200 && spaceAbove > spaceBelow
    const maxH = Math.max(120, Math.min(256, (openUp ? spaceAbove : spaceBelow) - 12))
    this.list.style.left = `${r.left}px`
    this.list.style.width = `${r.width}px`
    this.list.style.maxHeight = `${maxH}px`
    if (openUp) {
      this.list.style.top = "auto"
      this.list.style.bottom = `${window.innerHeight - r.top + gap}px`
    } else {
      this.list.style.bottom = "auto"
      this.list.style.top = `${r.bottom + gap}px`
    }
  }

  isOpen() { return !this.list.classList.contains("hidden") }

  inputClass() {
    const cls = this.select.className
    let size = "input-sm"
    if (/select-xs/.test(cls)) size = "input-xs"
    else if (/select-lg/.test(cls)) size = "input-lg"
    else if (/select-md/.test(cls)) size = "input-md"
    const wFull = /\bw-full\b/.test(cls) ? "w-full" : ""
    return `input input-bordered ${size} ${wFull}`.trim()
  }

  blankLabel() {
    const blank = Array.from(this.select.options).find((o) => o.value === "")
    return blank ? blank.text : null
  }
}
